<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {

  size?: 'lg' | 'md' | 'sm'

  direction?: 'column' | 'row'
  bare?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'lg',
  direction: 'column',
  bare: false,
})

const { t } = useI18n()

const showViewportStrip = computed(() => !props.bare && props.direction !== 'row')

const showInlineStrip = computed(() => !props.bare && props.direction === 'row')

const wrapperA11yAttrs = computed(() =>
  showViewportStrip.value
    ? {}
    : { role: 'status' as const, 'aria-busy': 'true' as const, 'aria-live': 'polite' as const },
)

const viewportAriaLabel = computed(() => t('general.loading'))
</script>

<template>
  <span
    v-if="bare"
    class="app-page-loader__viz"
    :class="[`app-page-loader__viz--${size}`, 'app-page-loader__viz--bare']"
    role="status"
    aria-busy="true"
    aria-live="polite"
  >
    <span class="app-page-loader__track-ring" aria-hidden="true" />
    <span class="app-page-loader__sweep" aria-hidden="true" />
    <span class="app-page-loader__simple-fallback" aria-hidden="true">
      <span class="app-page-loader__simple-fallback-ring" />
    </span>
  </span>

  <div
    v-else
    class="app-page-loader"
    :class="{ 'app-page-loader--row': direction === 'row' }"
    v-bind="wrapperA11yAttrs"
  >
    <Teleport to="body" :disabled="!showViewportStrip">
      <div
        v-if="showViewportStrip"
        class="app-page-loader__viewport-strip"
        role="status"
        aria-live="polite"
        aria-busy="true"
        :aria-label="viewportAriaLabel"
      >
        <div class="app-page-loader__viewport-track">
          <div class="app-page-loader__viewport-band" aria-hidden="true" />
          <div class="app-page-loader__viewport-gleam" aria-hidden="true" />
        </div>
      </div>
    </Teleport>

    <div v-if="showInlineStrip" class="app-page-loader__inline-strip" aria-hidden="true">
      <div class="app-page-loader__inline-track">
        <div class="app-page-loader__inline-chunk app-page-loader__inline-chunk--a" />
        <div class="app-page-loader__inline-chunk app-page-loader__inline-chunk--b" />
      </div>
    </div>

    <div v-if="$slots.default && !showViewportStrip" class="app-page-loader__slot">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.app-page-loader {
  --loader-accent: var(--backoffice-accent, #d8670c);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  color: var(--text-light, #6b7280);
}

.app-page-loader--row {
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
}

.app-page-loader__slot {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  text-align: center;
}

.app-page-loader--row .app-page-loader__slot {
  flex: 0 1 auto;
  text-align: left;
}

.app-page-loader__viewport-strip {
  position: fixed;
  top: 0;
  left: var(--backoffice-sidebar-width);
  right: 0;

  z-index: 500;
  pointer-events: none;
}

@media (max-width: 768px) {
  .app-page-loader__viewport-strip {

    left: 0;
  }
}

.app-page-loader__viewport-track {
  position: relative;
  height: 9px;
  width: 100%;
  overflow: hidden;
  container-type: inline-size;
  container-name: app-loader-viewport-track;
  background: transparent;
}

.app-page-loader__viewport-band {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    rgba(216, 103, 12, 0) 18%,
    rgba(234, 88, 12, 0.85) 38%,
    #ffb347 48%,
    #ea580c 52%,
    rgba(234, 88, 12, 0.85) 62%,
    rgba(216, 103, 12, 0) 82%,
    transparent 100%
  );
  background-size: 240% 100%;
  background-repeat: no-repeat;
  filter: drop-shadow(0 1px 10px rgba(234, 88, 12, 0.5));
  will-change: background-position;
  animation: app-loader-viewport-band-slide 1.55s cubic-bezier(0.42, 0.05, 0.58, 0.95) infinite;
}

.app-page-loader__viewport-gleam {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    90deg,
    transparent 35%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 65%
  );
  background-size: 260% 100%;
  background-repeat: no-repeat;
  opacity: 0.75;
  mix-blend-mode: screen;
  pointer-events: none;
  animation: app-loader-viewport-gleam-slide 0.85s cubic-bezier(0.52, 0, 0.48, 1) infinite;
}

@keyframes app-loader-viewport-band-slide {
  0% {
    background-position: 118% 0;
  }
  100% {
    background-position: -118% 0;
  }
}

@keyframes app-loader-viewport-gleam-slide {
  0% {
    background-position: 125% 0;
  }
  100% {
    background-position: -125% 0;
  }
}

.app-page-loader__inline-strip {
  flex: 1;
  min-width: 4rem;
  min-height: 6px;
}

.app-page-loader__inline-track {
  position: relative;
  height: 6px;
  width: 100%;
  border-radius: 3px;
  background: #cbd2df;
  overflow: hidden;
}

.app-page-loader__inline-chunk {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 2px;
  will-change: transform;
}

.app-page-loader__inline-chunk--a {
  width: 58%;
  background: linear-gradient(
    90deg,
    rgba(216, 103, 12, 0) 0%,
    rgba(216, 103, 12, 0.45) 18%,
    var(--loader-accent) 50%,
    #ea580c 65%,
    rgba(216, 103, 12, 0) 100%
  );
  box-shadow: 0 0 10px rgba(216, 103, 12, 0.35);
  animation: app-loader-inline-a 1.35s cubic-bezier(0.48, 0.14, 0.52, 0.86) infinite;
}

.app-page-loader__inline-chunk--b {
  width: 42%;
  opacity: 0.8;
  background: linear-gradient(
    90deg,
    rgba(255, 200, 170, 0),
    rgba(255, 214, 190, 0.95) 50%,
    rgba(255, 200, 170, 0)
  );
  animation: app-loader-inline-b 1.1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes app-loader-inline-a {
  0% {
    transform: translate3d(-100%, 0, 0);
  }
  100% {
    transform: translate3d(220%, 0, 0);
  }
}

@keyframes app-loader-inline-b {
  0% {
    transform: translate3d(-70%, 0, 0);
  }
  100% {
    transform: translate3d(280%, 0, 0);
  }
}

.app-page-loader__viz {
  --loader-accent: var(--backoffice-accent, #d8670c);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  vertical-align: middle;
}

.app-page-loader__viz--lg {
  width: 2.75rem;
  height: 2.75rem;
  --stroke: 4px;
}

.app-page-loader__viz--md {
  width: 2.35rem;
  height: 2.35rem;
  --stroke: 3.5px;
}

.app-page-loader__viz--sm {
  width: 1.35rem;
  height: 1.35rem;
  --stroke: 3px;
}

.app-page-loader__viz--bare.app-page-loader__viz--sm {
  width: 1.25rem;
  height: 1.25rem;
}

.app-page-loader__track-ring {
  position: absolute;
  inset: 0;
  z-index: 1;
  box-sizing: border-box;
  border-radius: 50%;
  border: var(--stroke) solid #dce1ea;
  pointer-events: none;
}

.app-page-loader__sweep {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 50%;
  box-sizing: border-box;
  background: conic-gradient(
    from -90deg,
    transparent 0deg 275deg,
    color-mix(in srgb, var(--loader-accent) 35%, transparent) 300deg,
    var(--loader-accent) 350deg,
    transparent 360deg
  );
  -webkit-mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--stroke)),
    #000 calc(100% - var(--stroke) + 0.5px)
  );
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--stroke)),
    #000 calc(100% - var(--stroke) + 0.5px)
  );
  animation: app-loader-ring-spin 0.85s linear infinite;
  pointer-events: none;
}

.app-page-loader__simple-fallback {
  display: none;
  position: absolute;
  inset: 0;
  z-index: 3;
  align-items: center;
  justify-content: center;
}

.app-page-loader__simple-fallback-ring {
  width: 72%;
  height: 72%;
  border-radius: 50%;
  border: 3px solid #dce1ea;
  border-top-color: var(--loader-accent, #d8670c);
  box-sizing: border-box;
}

@keyframes app-loader-ring-spin {
  to {
    transform: rotate(360deg);
  }
}

@supports not (mask: radial-gradient(farthest-side, transparent 50%, #000 51%)) {
  .app-page-loader__sweep {
    background: none;
    mask: none;
    -webkit-mask: none;
    border: var(--stroke) solid transparent;
    border-top-color: var(--loader-accent);
    border-right-color: color-mix(in srgb, var(--loader-accent) 45%, transparent);
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-page-loader__viewport-band,
  .app-page-loader__viewport-gleam {
    animation: none !important;
    background-position: 50% 0 !important;
    opacity: 0.72;
  }

  .app-page-loader__viewport-gleam {
    display: none;
  }

  .app-page-loader__inline-chunk--a,
  .app-page-loader__inline-chunk--b {
    animation: none !important;
    transform: none !important;
    opacity: 0.55;
    left: 35%;
    width: 30%;
  }

  .app-page-loader__inline-chunk--b {
    display: none;
  }

  .app-page-loader__sweep {
    animation: none !important;
  }

  .app-page-loader__track-ring,
  .app-page-loader__sweep {
    display: none;
  }

  .app-page-loader__simple-fallback {
    display: flex !important;
  }

  .app-page-loader__simple-fallback-ring {
    animation: app-loader-simple-spin 1.2s linear infinite !important;
  }

  .app-page-loader__viz--sm .app-page-loader__simple-fallback-ring {
    border-width: 2px;
  }
}

@keyframes app-loader-simple-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
